import { useId } from 'react';

/**
 * The one search field every console list narrows itself with.
 *
 * **It is a field beside the list it narrows, never a field in the bar.** The
 * catalog's `topbar_search` row withholds the top-bar field from the operator
 * console for exactly that reason: a bar-level field stands on every route
 * including the ones it cannot search, and submits to one list from all of
 * them. Four lists each carrying their own field is four questions each asked
 * where its answer renders.
 *
 * **The term lives in the caller's query string, not in this component.** An
 * operator's search is part of the address — copyable, reloadable, and
 * something Back steps out of — and a component holding it in state would make
 * the address lie about what is on screen.
 *
 * **Every narrowing is served by the owning service**, so a term describes the
 * whole list rather than the page in hand. A field that filtered fetched rows
 * would answer «لا نتيجة» about records that plainly exist, which reads as an
 * answer rather than as a ceiling.
 *
 * `type="search"` for the browser's own clear control, and the label is a real
 * `<label>` rather than a placeholder: a placeholder disappears the moment
 * something is typed, which is when a reader most needs to know what the field
 * searches.
 */
export function ListSearch({
  label,
  placeholder,
  value,
  onChange,
  hint,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
  /** What the term matches, where that is narrower than the reader would assume. */
  hint?: string;
}) {
  const fieldId = useId();
  const hintId = `${fieldId}-hint`;

  return (
    <div className="mb-4 grid gap-2">
      <label htmlFor={fieldId} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={fieldId}
        type="search"
        value={value}
        placeholder={placeholder}
        {...(hint ? { 'aria-describedby': hintId } : {})}
        onChange={(event) => onChange(event.target.value)}
        className="hit-target w-full max-w-measure rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink placeholder:text-ink-faint"
      />
      {hint ? (
        <p id={hintId} className="mb-0 text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
